import { customElement } from '@adobe/edex/ui/edex-lit';
import { html } from '@adobe/edex/ui/edex-lit';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-clock';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-user-group';
import '@spectrum-web-components/status-light/sp-status-light';

import { CourseCardSchema } from '@adobe/edex/ui/shared/types';
import { EdexTagName } from '@adobe/edex/ui/shared/constants';
import { BaseElement } from '@adobe/edex/ui/shared/components/base.element';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { getResizedImageURL } from '@adobe/edex/ui/shared/utils/imageResize';
import { getCourseURL } from '@adobe/edex/ui/shared/utils/routes';
import { getCourseStatus, getVariant, getTranslatedCourseStatus } from '@adobe/edex/ui/shared/utils';
import { CourseCardType } from '@adobe/edex/ui/constants';
import './course-card.scss';

@customElement(EdexTagName.courseCard)
export class CourseCard extends BaseElement {
  get useLightDom(): boolean {
    return true;
  }

  static get properties() {
    return {
      course: { type: Object },
      cardType: { attribute: false },
      checked: { type: Boolean },
    };
  }

  course: CourseCardSchema;
  cardType = CourseCardType.default;
  checked = false;

  handleCheckClick(event: Event) {
    this.emitEvent('cardSelected', {});
  }

  renderTitle() {
    const {
      course: { title, vanityURL, courseType },
    } = this;
    return vanityURL && courseType
      ? html`
          <a rel="external" target="_top" href="${getCourseURL(this.course)}">${$t(title)}</a>
        `
      : html`
          ${$t(title)}
        `;
  }

  renderDuration() {
    const {
      course: {
        meta: { duration },
      },
    } = this;
    return duration
      ? html`
          <p>
            ${/* TODO: translation */ ''}
            <span>${duration < 1 ? `${duration * 60} mins` : `${duration} hrs`}</span>
            <sp-icon size="s" aria-hidden="true">
              <sp-icon-clock></sp-icon-clock>
            </sp-icon>
          </p>
        `
      : null;
  }

  renderEnrollees() {
    const {
      course: { enrollees },
    } = this;
    return enrollees
      ? html`
          <p>
            <span>${enrollees}</span>
            <sp-icon size="s" aria-hidden="true">
              <sp-icon-user-group></sp-icon-user-group>
            </sp-icon>
          </p>
        `
      : null;
  }

  renderCourseCompledtedDuration(duration: number, progress: number) {
    let completedInMinutes = (duration * progress * 60) / 100;
    const completedHours = completedInMinutes >= 60 ? Math.floor(completedInMinutes / 60) : 0;
    completedInMinutes = Math.floor(completedInMinutes - completedHours * 60);
    if (completedHours > 0) {
      if (completedInMinutes > 0) {
        return html`
          ${completedHours}h ${completedInMinutes}m of ${duration < 1 ? `${duration * 60}m` : `${duration}h`}
        `;
      } else {
        return html`
          ${completedHours}h of ${duration < 1 ? `${duration * 60}m` : `${duration}h`}
        `;
      }
    } else {
      return html`
        ${completedInMinutes}m of ${duration < 1 ? `${duration * 60}m` : `${duration}h`}
      `;
    }
  }

  renderStatusAndCompletedDuration(enrollment) {
    const status = getCourseStatus(enrollment);
    const translatedStatus = getTranslatedCourseStatus(status);
    const {
      course: {
        meta: { duration, progress },
      },
    } = this;

    return html`
      <p>
        <span>
          <sp-status-light size="m" variant=${getVariant(status)}>
            ${translatedStatus}
            <span class="pipe_padding">|</span>
            ${this.renderCourseCompledtedDuration(duration, progress)}
            <sp-icon size="m" aria-hidden="true">
              <sp-icon-clock></sp-icon-clock>
            </sp-icon>
          </sp-status-light>
        </span>
      </p>
    `;
  }

  renderFooter() {
    const {
      course: {
        meta: { enrollment },
      },
    } = this;

    return enrollment
      ? html`
          ${this.renderStatusAndCompletedDuration(enrollment)} ${this.renderEnrollees()}
        `
      : html`
          ${this.renderDuration()} ${this.renderEnrollees()}
        `;
  }

  renderActionIcon() {
    const { cardType } = this;
    if (cardType === CourseCardType.checkbox) {
      return html`
        <sp-checkbox emphasized class="course-checkbox-icon" size="m" ?checked=${this.checked}></sp-checkbox>
      `;
    }
    return null;
  }

  renderActionButton() {
    const { cardType } = this;
    if (cardType === CourseCardType.checkbox) {
      return html`
        <sp-action-button
          slot="actions"
          placement="bottom-end"
          class="course-checkbox"
          @click=${this.handleCheckClick}
          focusable
        >
          <sp-icon slot="icon">${this.renderActionIcon()}</sp-icon>
        </sp-action-button>
      `;
    }

    return null;
  }

  render() {
    const {
      course: {
        assets: {
          images: {
            hero: { largeURL },
          },
        },
        courseType,
        title,
      },
    } = this;

    const heroImageURL = largeURL ? getResizedImageURL(largeURL, 600, 200) : '';

    return html`
      <div class="course-card__banner">
        ${heroImageURL &&
        html`
          <img alt="${title || ''}" loading="lazy" src="${heroImageURL}" width="600" height="200" />
        `}
        ${this.renderActionButton()}
      </div>
      <div class="course-card__content">
        <h5 class="body-header line-clamp-2">${this.renderTitle()}</h5>
        <p class="course-card__subheading text-ellipsis">${$t(courseType?.i18nLabel)}</p>
        <div class="course-card__footer">${this.renderFooter()}</div>
      </div>
    `;
  }
}
